import { Injectable } from '@angular/core'
import { Observable, Subject } from 'rxjs'
import {
  MATCH_COUNTS_FIELD,
  MatchTargetKey,
  RowOrigin,
} from '../models/source-key.model'
import { RowStatus, WorkbenchRow } from '../models/workbench-row.model'

/**
 * Origins that may be ignored. Prometheus (Custody + Trading) only, mirroring the design's action
 * matrix and the prototype's own `setIgnore`, which skips any row whose `src` is not `cust`/`trad`.
 * Kept in step with `utils/available-actions.ts`, which gates the menu item on the same rule.
 */
const IGNORABLE_ORIGINS: RowOrigin[] = ['cust', 'trad']

/** A group needs at least two members to mean anything; below that it dissolves. */
const MIN_GROUP_SIZE = 2

/**
 * In-memory reconciliation engine over the shared WorkbenchRow objects. Groups are represented by
 * the `group` id on each row; mutations happen in place (grids hold the same object references)
 * and `changes$` announces affected row ids so grids can refresh the relevant cells/rows.
 *
 * Positions 2.0 matching is explicit and group-based — it replaces 1.0's implicit
 * account x security matching, so a group may span any number of sources.
 */
@Injectable()
export class MatchingService {
  private readonly rows = new Map<string, WorkbenchRow>()
  private seq = 1
  private readonly changesSubject = new Subject<Set<string>>()
  readonly changes$: Observable<Set<string>> =
    this.changesSubject.asObservable()

  init(rows: WorkbenchRow[]): void {
    this.rows.clear()
    rows.forEach(row => this.rows.set(row.rowId, row))
  }

  /**
   * A manual Match action (`group` set) always wins, since it reflects what the user just did. With
   * no group yet, fall back to the server's own `matchStatus` — read-only until the API gives us
   * the matched rows' identities rather than just a verdict/count.
   */
  statusOf(row: WorkbenchRow): RowStatus {
    if (row.ignored) return 'ignored'
    if (row.group) return 'matched'
    return row.serverMatchStatus === 'MATCHED' ? 'matched' : 'unmatched'
  }

  groupMembers(group: string): WorkbenchRow[] {
    return [...this.rows.values()].filter(row => row.group === group)
  }

  /** Distinct origins other than the given rows' own, within the rows' groups. */
  otherOriginsFor(rowIds: string[]): RowOrigin[] {
    const rows = this.resolve(rowIds).filter(row => row.group)
    const groups = new Set(rows.map(row => row.group))
    const ownOrigins = new Set(rows.map(row => row.origin))
    const others = new Set<RowOrigin>()
    this.rows.forEach(row => {
      if (row.group && groups.has(row.group) && !ownOrigins.has(row.origin)) {
        others.add(row.origin)
      }
    })
    return [...others]
  }

  groupsFor(rowIds: string[]): string[] {
    const groups = this.resolve(rowIds)
      .map(row => row.group)
      .filter((g): g is string => !!g)
    return [...new Set(groups)]
  }

  /**
   * With a group, the live graph is authoritative (a manual Match/Unmatch can move faster than a
   * fresh fetch would reflect). With no group yet, fall back to the server's own count for that
   * target — real-time-accurate the moment a group forms, since the graph then takes over.
   */
  matchCount(row: WorkbenchRow, target: MatchTargetKey): number {
    if (!row.group)
      return row.serverMatchCounts?.[MATCH_COUNTS_FIELD[target]] ?? 0
    return this.groupMembers(row.group).filter(
      member => member.rowId !== row.rowId && member.origin === target,
    ).length
  }

  matchedRowIds(row: WorkbenchRow, target: MatchTargetKey): string[] {
    if (!row.group) return []
    return this.groupMembers(row.group)
      .filter(member => member.origin === target)
      .map(member => member.rowId)
  }

  /**
   * Sum a numeric property across the rows of one origin linked to the given row. Returns null when
   * the row is unmatched or has no linked rows of that origin, which the unit-difference columns
   * render as an empty cell rather than a zero.
   */
  sumLinked(
    row: WorkbenchRow,
    target: MatchTargetKey,
    pick: (linked: WorkbenchRow) => number | null | undefined,
  ): number | null {
    if (!row.group) return null
    const members = this.groupMembers(row.group).filter(
      member => member.rowId !== row.rowId && member.origin === target,
    )
    if (!members.length) return null
    return members.reduce(
      (total, member) => total + (Number(pick(member)) || 0),
      0,
    )
  }

  /**
   * Match/Match Additional: merge the selected rows into a single group. Any distinct existing
   * groups among the selection are merged so prior cross-source matches are preserved.
   */
  match(rowIds: string[]): void {
    const rows = this.resolve(rowIds)
    if (rows.length < MIN_GROUP_SIZE) return
    const existing = [
      ...new Set(rows.map(row => row.group).filter((g): g is string => !!g)),
    ]
    const group = existing[0] ?? `m${this.seq++}`
    const merge = new Set(existing)
    const affected = new Set<string>()
    this.rows.forEach(row => {
      if (rowIds.includes(row.rowId) || (row.group && merge.has(row.group))) {
        if (row.group !== group || row.ignored) affected.add(row.rowId)
        row.group = group
        row.ignored = false
      }
    })
    this.emit(affected)
  }

  /** Detach the whole group(s) the given rows belong to. */
  unmatchGroups(groups: string[]): void {
    const set = new Set(groups)
    const affected = new Set<string>()
    this.rows.forEach(row => {
      if (row.group && set.has(row.group)) {
        affected.add(row.rowId)
        row.group = undefined
      }
    })
    this.emit(affected)
  }

  /**
   * Detach only the members of a given origin from the group(s), dissolving orphaned groups.
   *
   * NOT CURRENTLY DISPATCHED. `POST /flex-positions/actions` has no detach-one-member primitive —
   * an `unmatch` dissolves every group the selection resolves to, and the request carries no
   * origin — so the grid applies {@link unmatchGroups} for every Unmatch branch, including the
   * modal's per-source choice, rather than showing a local state the server did not produce. Kept
   * (and kept covered) because the modal that would drive it is still built and its per-source
   * options become reachable the moment perseus grows the primitive.
   */
  unmatchOrigin(groups: string[], origin: RowOrigin): void {
    const set = new Set(groups)
    const affected = new Set<string>()
    this.rows.forEach(row => {
      if (row.group && set.has(row.group) && row.origin === origin) {
        affected.add(row.rowId)
        row.group = undefined
      }
    })
    groups.forEach(group => {
      const remaining = this.groupMembers(group)
      if (remaining.length < MIN_GROUP_SIZE) {
        remaining.forEach(row => {
          affected.add(row.rowId)
          row.group = undefined
        })
      }
    })
    this.emit(affected)
  }

  /** Ignore/Unignore. Prometheus rows only; ignoring also detaches the row from its group. */
  ignore(rowIds: string[], value: boolean): void {
    const affected = new Set<string>()
    this.resolve(rowIds).forEach(row => {
      if (!IGNORABLE_ORIGINS.includes(row.origin)) return
      if (value) {
        row.ignored = true
        row.group = undefined
      } else {
        row.ignored = false
      }
      affected.add(row.rowId)
    })
    this.emit(affected)
  }

  private resolve(rowIds: string[]): WorkbenchRow[] {
    return rowIds
      .map(id => this.rows.get(id))
      .filter((row): row is WorkbenchRow => !!row)
  }

  private emit(affected: Set<string>): void {
    if (affected.size) this.changesSubject.next(affected)
  }
}
